import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import BopBackLink from '../../components/BopBackLink';
import { apiFetch, apiDownload } from '../../lib/api';
import { JobPec, DestinatarioPec, TentativoPec, AllegatoJob } from '../../types';

const STATO_JOB_BADGE: Record<string, string> = {
  BOZZA:      'bop-badge--neutral',
  IN_CORSO:   'bop-badge--info',
  COMPLETATO: 'bop-badge--success',
  ERRORE:     'bop-badge--danger',
};

const STATO_DEST_BADGE: Record<string, string> = {
  IN_ATTESA: 'bop-badge--neutral',
  INVIATO:   'bop-badge--success',
  ERRORE:    'bop-badge--danger',
};

export default function DettaglioJobPec() {
  const { id }   = useParams<{ id: string }>();

  const [job,         setJob]         = useState<JobPec | null>(null);
  const [destinatari, setDestinatari] = useState<DestinatarioPec[]>([]);
  const [allegati,    setAllegati]    = useState<AllegatoJob[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [expanded,    setExpanded]    = useState<Set<number>>(new Set());

  // Reinvio modal state
  const [reinvioDestId,       setReinvioDestId]       = useState<number | null>(null);
  const [emailAlternativa,    setEmailAlternativa]    = useState('');
  const [useEmailAlt,         setUseEmailAlt]         = useState(false);
  const [reinviando,          setReinviando]          = useState(false);
  const [reinvioError,        setReinvioError]        = useState('');

  const fetchJob = () =>
    apiFetch<{ job: JobPec; destinatari: DestinatarioPec[] }>(`/api/pec-massiva/jobs/${id}`)
      .then((d) => { setJob(d.job); setDestinatari(d.destinatari); })
      .catch((e) => setError(e.message));

  const fetchAllegati = () =>
    apiFetch<{ allegati: AllegatoJob[] }>(`/api/pec-massiva/jobs/${id}/allegati`)
      .then((d) => setAllegati(d.allegati))
      .catch(() => {/* silent — no GCS bucket in dev */});

  // Fetch iniziale
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchJob(), fetchAllegati()]).finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh ogni 5s se il job è IN_CORSO
  useEffect(() => {
    if (job?.statoJob !== 'IN_CORSO') return;
    const interval = setInterval(fetchJob, 5000);
    return () => clearInterval(interval);
  }, [job?.statoJob]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExpanded = (destId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(destId)) next.delete(destId); else next.add(destId);
      return next;
    });
  };

  const openReinvio = (destId: number) => {
    setReinvioDestId(destId);
    setEmailAlternativa('');
    setUseEmailAlt(false);
    setReinvioError('');
  };

  const closeReinvio = () => { setReinvioDestId(null); setReinvioError(''); };

  const handleReinvia = async () => {
    if (!reinvioDestId) return;
    setReinviando(true);
    setReinvioError('');
    try {
      const body: Record<string, string> = {};
      if (useEmailAlt && emailAlternativa.trim()) {
        body.emailAlternativa = emailAlternativa.trim();
      }
      const data = await apiFetch<{ destinatario: DestinatarioPec; statoInvio: string }>(
        `/api/pec-massiva/jobs/${id}/destinatari/${reinvioDestId}/reinvia`,
        { method: 'POST', body: JSON.stringify(body) }
      );
      setDestinatari((prev) =>
        prev.map((d) => d.id === reinvioDestId ? data.destinatario : d)
      );
      closeReinvio();
    } catch (e: any) {
      setReinvioError(e.message);
    } finally {
      setReinviando(false);
    }
  };

  if (loading) return <Layout><div className="inline-loading">Caricamento…</div></Layout>;
  if (error)   return <Layout><div className="inline-error">{error}</div></Layout>;
  if (!job)    return <Layout><div className="bop-empty-state">Job non trovato.</div></Layout>;

  const pct = job.totaleDestinatari > 0
    ? Math.round((job.inviati / job.totaleDestinatari) * 100)
    : 0;

  const reinvioDest = reinvioDestId ? destinatari.find((d) => d.id === reinvioDestId) : null;

  return (
    <Layout title={`Job Email #${job.id}`}>
      {/* Back */}
      <BopBackLink to="/tecnico/pec-massiva" label="Torna alla PEC massiva" />

      {/* ── Scheda job ──────────────────────────────────────────────── */}
      <div className="bop-card bop-job-pec__card">
        <div className="bop-job-pec__detail-grid">
          <div>
            <div className="bop-job-pec__label">Oggetto</div>
            <div className="bop-job-pec__value--title">{job.oggetto}</div>
          </div>
          <div>
            <div className="bop-job-pec__label">Società</div>
            <div className="bop-job-pec__value">{job.societaNome || '—'}</div>
          </div>
          <div>
            <div className="bop-job-pec__label">Stato</div>
            <span className={`bop-badge ${STATO_JOB_BADGE[job.statoJob] ?? 'bop-badge--neutral'}`}>{job.statoJob}</span>
          </div>
          <div>
            <div className="bop-job-pec__label">SMTP</div>
            <div className="bop-job-pec__value">{job.smtpHost || '—'}</div>
          </div>
          <div>
            <div className="bop-job-pec__label">Creato il</div>
            <div className="bop-job-pec__value">{new Date(job.createdAt).toLocaleString('it-IT')}</div>
          </div>
          <div>
            <div className="bop-job-pec__label bop-job-pec__label--spaced">
              Progresso — {job.inviati}/{job.totaleDestinatari}
              {job.errori > 0 && <span className="bop-job-pec__error-count">{job.errori} errori</span>}
            </div>
            <div className="bop-job-pec__progress-track">
              <div
                className={`bop-job-pec__progress-bar ${job.errori > 0 && job.inviati === 0 ? 'bop-job-pec__progress-bar--danger' : 'bop-job-pec__progress-bar--primary'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="bop-job-pec__progress-pct">{pct}%</div>
          </div>
        </div>

        {/* Testo */}
        <div className="bop-job-pec__testo-section">
          <div className="bop-job-pec__label bop-job-pec__label--spaced">Testo</div>
          <pre className="bop-job-pec__testo-pre">{job.testo}</pre>
        </div>
      </div>

      {/* ── Allegati ─────────────────────────────────────────────────── */}
      {allegati.length > 0 && (
        <div className="bop-card bop-job-pec__section-card">
          <div className="bop-job-pec__section-header">
            <span className="bop-job-pec__section-title">Allegati ({allegati.length})</span>
          </div>
          <div className="bop-table-wrapper">
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Nome file</th>
                  <th className="bop-job-pec__col-download"></th>
                </tr>
              </thead>
              <tbody>
                {allegati.map((a, i) => (
                  <tr key={i}>
                    <td>{a.nome}</td>
                    <td>
                      <button
                        className="bop-btn bop-btn-ghost bop-job-pec__btn-download"
                        onClick={() => apiDownload(`/api/pec-massiva/jobs/${id}/allegati/${encodeURIComponent(a.nome)}`, a.nome)}
                      >
                        ↓ Scarica
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Tabella destinatari ──────────────────────────────────────── */}
      <div className="bop-card">
        <div className="bop-job-pec__section-header">
          <span className="bop-job-pec__section-title">Destinatari ({destinatari.length})</span>
          {job.statoJob === 'IN_CORSO' && (
            <span className="bop-job-pec__auto-refresh">• Aggiornamento automatico ogni 5s</span>
          )}
        </div>
        <div className="bop-table-wrapper">
          {destinatari.length === 0 ? (
            <div className="bop-empty-state">Nessun destinatario.</div>
          ) : (
            <table className="bop-table">
              <thead>
                <tr>
                  <th className="bop-job-pec__col-expand"></th>
                  <th>Email Originale</th>
                  <th className="bop-job-pec__col-stato">Stato</th>
                  <th className="bop-job-pec__col-tentativi">Tentativi</th>
                  <th className="bop-job-pec__col-azioni">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {destinatari.map((d) => (
                  <React.Fragment key={d.id}>
                    {/* Riga principale destinatario */}
                    <tr>
                      <td className="bop-job-pec__expand-cell">
                        {d.tentativi.length > 0 && (
                          <button
                            onClick={() => toggleExpanded(d.id)}
                            className="bop-job-pec__expand-btn"
                            title={expanded.has(d.id) ? 'Comprimi' : 'Espandi tentativi'}
                          >
                            {expanded.has(d.id) ? '▼' : '▶'}
                          </button>
                        )}
                      </td>
                      <td>{d.emailOriginale}</td>
                      <td className="bop-job-pec__td-stato">
                        <span className={`bop-badge ${STATO_DEST_BADGE[d.stato] ?? 'bop-badge--neutral'}`}>{d.stato}</span>
                      </td>
                      <td className="bop-job-pec__td-tentativi">
                        {d.tentativi.length}
                        {d.totaleReinvii > 0 && (
                          <span className="bop-job-pec__reinvii-count">({d.totaleReinvii} reinvii)</span>
                        )}
                      </td>
                      <td>
                        {d.stato === 'ERRORE' && (
                          <button
                            className="bop-btn bop-btn-secondary bop-job-pec__btn-reinvia"
                            onClick={() => openReinvio(d.id)}
                          >
                            Reinvia
                          </button>
                        )}
                      </td>
                    </tr>

                    {/* Righe tentativi (espanso) */}
                    {expanded.has(d.id) && d.tentativi.map((t: TentativoPec) => (
                      <tr key={`t-${t.id}`} className="bop-job-pec__tentativo-row">
                        <td className="bop-job-pec__tentativo-empty"></td>
                        <td colSpan={4} className="bop-job-pec__tentativo-detail">
                          <div className="bop-job-pec__tentativo-flex">
                            <span className="bop-job-pec__tentativo-num">#{t.tentativoNumero}</span>
                            <span className="bop-job-pec__tentativo-email">{t.emailUsata}</span>
                            <span className={`bop-badge ${STATO_DEST_BADGE[t.stato] ?? 'bop-badge--neutral'} bop-job-pec__badge--xs`}>
                              {t.stato}
                            </span>
                            {t.reinviatoManualmente && (
                              <span className="bop-job-pec__badge-manuale">
                                Manuale
                              </span>
                            )}
                            {t.errorMessage && (
                              <span className="bop-job-pec__tentativo-error">{t.errorMessage}</span>
                            )}
                            <span className="bop-job-pec__tentativo-date">
                              {t.inviatoAt ? new Date(t.inviatoAt).toLocaleString('it-IT') : '—'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Modal reinvio ────────────────────────────────────────────── */}
      {reinvioDestId && reinvioDest && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-job-pec__modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">Reinvia Email</h3>
              <button className="bop-modal__close" onClick={closeReinvio}>×</button>
            </div>
            <div className="bop-modal__body">
              <div className="bop-form-group">
                <div className="bop-job-pec__label">Email originale</div>
                <div className="bop-job-pec__value--email">{reinvioDest.emailOriginale}</div>
              </div>
              <div className="bop-form-group">
                <label className="form-check">
                  <input
                    type="checkbox"
                    checked={useEmailAlt}
                    onChange={(e) => setUseEmailAlt(e.target.checked)}
                  />
                  Usa email alternativa
                </label>
              </div>
              {useEmailAlt && (
                <div className="bop-form-group">
                  <label className="bop-label">Email alternativa</label>
                  <input
                    type="email"
                    className="bop-input"
                    value={emailAlternativa}
                    onChange={(e) => setEmailAlternativa(e.target.value)}
                    placeholder="email@esempio.it"
                    autoFocus
                  />
                </div>
              )}
              {reinvioError && <div className="inline-error">{reinvioError}</div>}
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={closeReinvio} disabled={reinviando}>
                Annulla
              </button>
              <button className="bop-btn bop-btn-primary" onClick={handleReinvia} disabled={reinviando}>
                {reinviando ? 'Invio in corso…' : 'Invia ora'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
